import cx from 'classnames'
import Parser from 'html-react-parser'
import _ from '@/utils/lodash-optimized'
import { useState, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'

import { Multi } from '../../../../igao/components/selects'
import {
  capitalize,
  relationType,
  findDiff
} from '../../../../igao/utils'
import { PlainButton } from '../../../../shared/Button'
import { Checkbox, Button, PortedModal } from '../../../../shared/NarusMaterial'
import { onTemplateChange } from '../../../controller'
import SelectRelations from '../../select-relations'

import styles from './edit-item.module.scss'

const Item = ({
  question,
  lists,
  applyChanges,
  setEditableQuestion
}) => {
  const [ modal, setModal ] = useState(null)
  const inputRef = useRef(null)
  const [ editable, setEditable ] = useState(question)
  const igao = editable.type !== 'plain'
  const equalItem = _.isEqual(editable, question)

  const onCancel = () => {
    if (equalItem) {
      setEditableQuestion(null)
      setEditable(null)
      return
    }
    setModal({
      title:
        'It looks like you have been editing something. If you leave before saving, your changes will be lost.',
      open: true,
      actions: [
        <Button
          key={1}
          label='Cancel'
          onClick={() => setModal(null)}
          grey
        />,
        <Button
          key={2}
          label='Discard'
          onClick={() => {
            setEditableQuestion(null)
            setEditable(null)
            setModal(null)
          }}
        />
      ]
    })
  }

  const preparedTemplate =
    igao &&
    editable.template
      ?.replaceAll("<span style='font-weight: bold;'>%%", '{')
      .replaceAll('%%</span>', '}')

  const editField = ({ id, key }) => {
    const tempQuestion = { ...editable }
    tempQuestion[key] = typeof value === 'object' ? id.id : id
    setEditable(tempQuestion)
  }

  let disableButton
  if (!equalItem) {
    if (igao) {
      disableButton =
        editable.template?.length > 0 &&
        editable.goal?.length > 0 &&
        editable.label?.length > 0 &&
        editable.domain &&
        editable.unfavorable?.length > 0
    } else disableButton = editable?.label?.trim().length > 0
  } else disableButton = false

  const issuePreview = () => {
    let preview = preparedTemplate
    lists.relations?.map(rel => {
      preview = preview?.replace(`{${rel.name}}`, () =>
        ReactDOMServer.renderToString(
          <Multi
            type='multi-select'
            disabled
            style={{ margin: '0 3px' }}
            relation={{
              type: relationType(rel.name),
              key: rel.name,
              label: capitalize(rel.name.replaceAll('_', ' | '))
            }}
            lists={lists}
          />
        ))
      return rel
    })
    return Parser(preview || '') || ''
  }

  const handleOnKeyDown = (e, ref, _item) => {
    setEditable(onTemplateChange(e, ref, _item, lists))
  }

  const selected = editable.domain
    ? {
      ...lists?.domain?.find(d => d.id === editable.domain),
      key: 'domain'
    }
    : null

  const getType = () => {
    if (!igao) {
      return editable?.relations?.length > 0 ? 'dynamic' : 'static'
    }
    return 'plain'
  }

  const disabled = !question.draft

  return (
    <div className={styles.editableContainer}>
      <label
        htmlFor='question'
        className={cx({ [styles.modified]: findDiff(editable, 'label') })}
      >
        Question
      </label>
      <input
        type='text'
        name='question'
        value={editable?.label}
        onChange={e =>
          editField({
            id: e.target.value,
            key: 'label'
          })}
      />
      {!disabled && (
        <div style={{ marginTop: '20px' }}>
          <Checkbox
            value={!igao}
            label='Does not trigger IGAO'
            onChange={() => {
              editField({
                key: 'type',
                id: getType()
              })
            }}
          />
        </div>
      )}
      {editable.type !== 'plain' && (
        <>
          <label
            htmlFor='issue_template'
            className={cx({ [styles.modified]: findDiff(editable, 'template') })}
          >
            Issue Template
          </label>
          <input
            type='text'
            name='issue_template'
            ref={inputRef}
            value={preparedTemplate}
            onChange={e =>
              editField({
                id: e.target.value,
                key: 'template'
              })}
            onKeyDown={e => handleOnKeyDown(e, inputRef, editable)}
          />
          <SelectRelations
            question={editable}
            inputRef={inputRef}
            setQuestion={setEditable}
            lists={lists}
          />
          {editable.template?.length > 0 && (
            <div
              key={editable.template.length}
              style={{
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingTop: '9px',
                color: '#9a9797',
                overflowWrap: 'anywhere',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {issuePreview()}
            </div>
          )}
          <div
            style={{
              margin: '18px 0',
              display: 'flex'
            }}
          >
            <div>
              <label
                htmlFor='multiselect'
                className={cx({ [styles.modified]: findDiff(editable, 'domain') })}
              >
                Domain
              </label>
              <Multi
                id='multiselect'
                type='multi-select'
                relative
                relation={{
                  key: 'domain',
                  label: 'Domain'
                }}
                lists={lists}
                itemId={editable.id}
                handler={editField}
                selected={selected}
              />
            </div>
            <div style={{ marginLeft: '16px' }}>
              <label htmlFor='checkboxes'>Unfavorable answer that triggers IGAO</label>
              <div
                style={{ padding: '6px 10px 14px 10px' }}
                id='checkboxes'
              >
                <Checkbox
                  disabled={disabled}
                  value={editable?.unfavorable === 'yes'}
                  label='Yes'
                  round
                  onChange={() => {
                    editField({
                      key: 'unfavorable',
                      id: 'yes'
                    })
                  }}
                />
                <Checkbox
                  disabled={disabled}
                  value={editable?.unfavorable === 'no'}
                  label='No'
                  round
                  onChange={() => {
                    editField({
                      key: 'unfavorable',
                      id: 'no'
                    })
                  }}
                />
              </div>
            </div>
          </div>
          <label
            htmlFor='Goal'
            className={cx({ [styles.modified]: findDiff(editable, 'goal') })}
          >
            Goal
          </label>
          <input
            type='text'
            name='Goal'
            value={editable?.goal}
            onChange={e =>
              editField({
                id: e.target.value,
                key: 'goal'
              })}
          />
        </>
      )}
      <div className={styles.bottomContainer}>
        <div>
          Question ID:
          {question.id}
        </div>
        <div className={styles.buttons}>
          <PlainButton
            label='Cancel'
            onClick={onCancel}
          />
          <PlainButton
            label='Save'
            style={{ marginLeft: '12px' }}
            disabled={!disableButton}
            btnType='contained'
            color='primary'
            onClick={() => {
              applyChanges(onTemplateChange(null, null, editable, lists))
            }}
          />
        </div>
      </div>
      <PortedModal
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
    </div>
  )
}

export default Item
